/**
 * Button - Multi-variant button component
 *
 * Comprehensive button component supporting multiple sizes, themes, variations,
 * and special types. Handles both button and link (`<a>`) rendering.
 *
 * WHY so many variants:
 * - App has diverse button styles (primary, secondary, danger, text-only)
 * - Different contexts require different sizes (small CTA, large hero)
 * - Theme integration for multi-tenant branding
 * - Icon support for visual enhancement
 *
 * Variants:
 * - Sizes: small, medium, large
 * - Themes: primary, secondary, dark, danger, default, heroInfo, disabled
 * - Variations: default, fullwidth, fullwidthmobile, outline, reversedOutline, text
 * - Special types: default, icon, form
 *
 * Accessibility:
 * - Support for aria-label and aria-labelledby
 * - Disabled state with cursor: not-allowed
 * - Semantic button vs anchor handling
 *
 * @see src/features/authentication/*.jsx - Auth buttons
 * @see src/features/payments/*.jsx - Checkout buttons
 * @see src/ui/ButtonIcon.jsx - Icon-specific button variant
 */
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import React from 'react';
import {
  primaryButtonHover,
  secondaryButtonHover,
  dangerButtonHover,
} from './mixins';

/**
 * Button size variants
 */
type ButtonSize = 'small' | 'medium' | 'large';

/**
 * Button theme variants
 */
type ButtonTheme = 'primary' | 'secondary' | 'dark' | 'danger' | 'default' | 'heroInfo' | 'disabled';

/**
 * Button variation styles
 */
type ButtonVariation = 'default' | 'fullwidth' | 'fullwidthmobile' | 'outline' | 'reversedOutline' | 'text';

/**
 * Button special types
 */
type ButtonSpecialType = 'default' | 'icon' | 'form';

/**
 * Button text size variants
 */
type ButtonTextSize = 'small';

/**
 * Button component props
 */
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button content */
  children?: React.ReactNode;

  /** Optional icon element */
  icon?: React.ReactNode;

  /** Button variation style */
  variation?: ButtonVariation;

  /** Button special type */
  specialType?: ButtonSpecialType;

  /** Button size */
  size?: ButtonSize;

  /** Button theme */
  theme?: ButtonTheme;

  /** Custom text color override */
  $textcolor?: string;

  /** Click handler */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;

  /** Render as anchor link (external URL) */
  href?: string;

  /** Button type attribute */
  type?: 'button' | 'submit' | 'reset';

  /** Disabled state */
  disabled?: boolean;

  /** Inline styles */
  style?: React.CSSProperties;

  /** Additional CSS classes */
  className?: string;

  /** ARIA label for accessibility */
  ariaLabel?: string;

  /** ARIA labelledby for accessibility */
  ariaLabelledby?: string;
}

/**
 * StyledButton props (internal - with $ prefixed transient props)
 */
interface StyledButtonProps {
  $variation: ButtonVariation;
  $specialtype: ButtonSpecialType;
  $textcolor?: string;
  size: ButtonSize;
  theme: ButtonTheme;
  text?: ButtonTextSize;
}

/**
 * Size variant styles
 *
 * WHY three distinct sizes:
 * - small: Mobile CTAs, compact buttons (100% width, uppercase, bold)
 * - medium: Default form buttons, inline actions
 * - large: Primary CTAs, hero sections (min 196px, responsive)
 */
const sizes: Record<ButtonSize, FlattenSimpleInterpolation> = {
  small: css`
    height: 5.6rem;
    min-width: 100%;
    text-transform: uppercase;
    text-align: center;
    border-radius: 2.5rem;
    font-size: 1.6rem;
    font-weight: var(--font-weight-800);
    letter-spacing: 0.48px;
    width: 100%;
  `,
  medium: css`
    font-size: 1.4rem;
    padding: 1.2rem 1.6rem;
    font-weight: var(--font-weight-500);
    height: 48px;
  `,
  large: css`
    padding: 1.6rem 3.2rem;
    display: flex;
    min-width: 196px;
    gap: 0.625rem;
    flex-shrink: 0;
    height: 48px;

    @media screen and (max-width: 479px) {
      width: 100%;
    }
  `,
};

/**
 * Special type styles
 *
 * WHY special types:
 * - form: Full width for form submissions
 * - icon: Minimal styling for icon-only buttons
 * - default: No special styling
 */
const specialTypes: Record<ButtonSpecialType, FlattenSimpleInterpolation> = {
  default: css``,
  icon: css``,
  form: css`
    width: 100% !important;
  `,
};

/**
 * Text size variant styles
 *
 * WHY text size variants:
 * - Override default button text sizing
 * - Better typography control for specific contexts
 * - Theme-aware font family integration
 */
const text: Record<ButtonTextSize, FlattenSimpleInterpolation> = {
  small: css`
    font-family: var(--theme-font-family) !important;
    font-size: 16px !important;
    font-style: normal !important;
    font-weight: var(--font-weight-400) !important;
    line-height: 150% !important; /* 24px */
  `,
};

/**
 * Variation styles
 *
 * WHY so many variations:
 * - fullwidth: Modal buttons, mobile CTAs
 * - fullwidthmobile: Responsive full-width on small screens only
 * - outline: Secondary actions with border styling
 * - text: Minimal text-only buttons for tertiary actions
 */
const variation: Record<ButtonVariation, FlattenSimpleInterpolation> = {
  default: css``,
  fullwidth: css`
    width: 100%;
  `,
  fullwidthmobile: css`
    @media screen and (max-width: 479px) {
      width: calc(100% - 1.6rem);
      min-width: unset;
      margin: 0 auto;
    }
  `,
  outline: css`
    background-color: #fff;
    box-shadow: none;
    color: inherit;
    color: var(--theme-action-primary);

    &:hover {
      background-color: #fff;
      box-shadow: none;
    }
  `,

  reversedOutline: css``,
  text: css`
    font-size: 1.6rem;
    background-color: transparent !important;
    border: none;
    box-shadow: none;
    padding: 0;
    text-transform: none;
    justify-content: center;

    transition: background-color 0.2s ease-in-out;

    &:hover {
      border-radius: 4px;
      background-color: var(
        --color-surface-action-neutral-hover,
        rgba(255, 255, 255, 0.05)
      );
      box-shadow: none;
      padding: 0;
    }
  `,
};

/**
 * Theme styles
 *
 * WHY extensive theme support:
 * - Multi-tenant branding (theme-action-primary variable)
 * - Semantic colors (primary, secondary, danger)
 * - Dark mode support (dark theme variant)
 * - Disabled state for form validation
 *
 * Theme integration:
 * - Uses CSS custom properties for branding
 * - Linear gradient overlays for hover states
 * - Consistent 0.2s transition timing
 */
const theme: Record<ButtonTheme, FlattenSimpleInterpolation | ((props: StyledButtonProps) => FlattenSimpleInterpolation)> = {
  disabled: css`
    background: linear-gradient(
        0deg,
        var(--surface-action-neutral-disabled, rgba(255, 255, 255, 0.02)) 0%,
        var(--surface-action-neutral-disabled, rgba(255, 255, 255, 0.02)) 100%
      ),
      var(--surface-action-neutral, #262935);
    color: var(--text-disabled);
    border: none;
    border-radius: var(--radius-sm, 4px);
  `,
  primary: css`
    color: var(--theme-text-color, #fff);
    background-color: var(--theme-action-primary);
    border: none;

    transition: background 0.2s ease-in-out;

    &:hover:not(:disabled) {
      ${primaryButtonHover}
      border: none;
    }

    &:disabled {
      opacity: 0.5;
    }
  `,
  secondary: css`
    color: var(--color-grey-600);
    background-color: var(--theme-action-secondary);
    border: none;

    &:hover:not(:disabled) {
      ${secondaryButtonHover}
      border: none;
    }
  `,
  dark: css`
    color: #fff;
    background-color: var(--color-surface-action-neutral);
    border: none;

    &:hover:not(:disabled) {
      background: linear-gradient(
          0deg,
          rgba(255, 255, 255, 0.05) 0%,
          rgba(255, 255, 255, 0.05) 100%
        ),
        var(--color-surface-action-neutral);
      border: none;
    }

    &:disabled {
      color: var(--text-disabled);
    }
  `,
  danger: (props: StyledButtonProps) => css`
    color: #e21a24 !important;
    background-color: var(--color-red-700);
    border: none;

    &:hover:not(:disabled) {
      opacity: 0.9;
      ${props.$variation !== 'outline' &&
      css`
        ${dangerButtonHover}
        border: none;
      `}
    }
  `,
  default: (props: StyledButtonProps) => css`
    /* color: #2a72f5;
    border-color: #2a72f5; */
    color: #2a72f5 !important;
    border-color: #2a72f5;

    ${props.disabled &&
    css`
      border-color: #999;
      color: #999 !important;
    `}
  `,
  heroInfo: css`
    color: var(--color-grey-100);
    background-color: #000;
    border-radius: 100px;
    border: 1px solid var(--gradient-light-stroke, rgba(255, 255, 255, 0.3));
  `,
};

/**
 * Styled button component
 *
 * WHY transient props ($variation, $specialtype, $textcolor):
 * - Prevents passing to DOM (styled-components 5.1+ feature)
 * - Avoids React warnings about unknown props
 * - Cleaner DOM output
 *
 * WHY pointer-events: all on disabled:
 * - Allows tooltips on disabled buttons
 * - Better UX for explaining why button is disabled
 * - Cursor: not-allowed still shows
 */
const StyledButton = styled.button<StyledButtonProps>`
  font-family: var(--theme-font-family);
  font-size: 14px;
  font-style: normal;
  font-weight: var(--font-weight-500);
  letter-spacing: 0.6px;
  line-height: 160%;
  text-align: center;
  border-radius: var(--radius-sm);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--transparencywhite-30);

  &:disabled {
    cursor: not-allowed;
    pointer-events: all !important;
  }

  @media screen and (min-width: 768px) and (max-width: 991px) {
    ${
      '' /* font-size: 16px;
    margin: 0 auto; */
    }
  }

  ${(props) =>
    props.$textcolor &&
    css`
      color: ${props.$textcolor} !important;
    `}

  ${(props) => sizes[props.size]}
  ${(props) => typeof theme[props.theme] === 'function'
    ? (theme[props.theme] as (props: StyledButtonProps) => FlattenSimpleInterpolation)(props)
    : theme[props.theme]}
  ${(props) => specialTypes[props.$specialtype]}
  ${(props) => variation[props.$variation]}
  ${(props) => props.text ? text[props.text] : ''}
`;

/**
 * Button component
 *
 * WHY href conditional rendering:
 * - External links need <a> tag for SEO and browser behavior
 * - Internal navigation handled by React Router elsewhere
 * - Maintains button styling with anchor semantics
 *
 * WHY icon && icon check:
 * - Prevents rendering undefined/null
 * - Allows optional icon prop
 * - Clean JSX output
 *
 * @param props - ButtonProps
 */
function Button({
  children,
  icon,
  variation = 'default',
  specialType = 'default',
  size = 'large',
  theme = 'primary',
  $textcolor,
  onClick,
  href,
  type,
  disabled,
  style,
  className,
  ariaLabel = '',
  ariaLabelledby = '',
  ...rest
}: ButtonProps): JSX.Element {
  // WHY separate anchor rendering:
  // - External links need target="_blank" and rel="noreferrer"
  // - Security best practice for external links
  // - Maintains button styling with link semantics
  if (href)
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        style={{ ...style }}
        className={className}
      >
        <StyledButton
          // style={{ ...style }}
          $variation={variation}
          $specialtype={specialType}
          size={size}
          theme={theme}
          $textcolor={$textcolor}
          onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
          type={type}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          {...rest}
        >
          {children}
          {icon && icon}
        </StyledButton>
      </a>
    );

  return (
    <StyledButton
      style={{ ...style }}
      $variation={variation}
      $specialtype={specialType}
      size={size}
      theme={theme}
      $textcolor={$textcolor}
      onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
      type={type}
      disabled={disabled}
      className={className}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      {...rest}
    >
      {children}
      {icon && icon}
    </StyledButton>
  );
}

export default Button;
